import {
  bulkSubscribeToServiceRequests,
  bulkUnsubscribeFromServiceRequests,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import { useEffect, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';

interface BulkSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Service requests the action applies to; its length drives the prompt. */
  serviceRequestIds: string[];
  /** Runs after a successful subscribe/unsubscribe (e.g. clear selection, refetch). */
  onSuccess?: () => void | Promise<void>;
}

type PendingAction = 'subscribe' | 'unsubscribe' | null;

/**
 * Subscribes or unsubscribes the current user to/from the selected service request
 * threads in bulk. Self-contained: it calls the API, surfaces the result toast, and
 * runs `onSuccess`. Both controls disable while an action is in flight; the modal
 * closes on success and stays open (showing the error) on failure.
 */
export const BulkSubscriptionModal = ({
  isOpen,
  onClose,
  serviceRequestIds,
  onSuccess,
}: BulkSubscriptionModalProps) => {
  const { setNotification } = useNotification();
  const [pending, setPending] = useState<PendingAction>(null);
  const isBusy = pending !== null;

  useEffect(() => {
    if (isOpen) setPending(null);
  }, [isOpen]);

  const run = async (action: 'subscribe' | 'unsubscribe') => {
    if (!serviceRequestIds.length || isBusy) return;

    setPending(action);
    try {
      if (action === 'subscribe') {
        const result = await bulkSubscribeToServiceRequests({
          serviceRequestIds,
        });
        if (!result.success) {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message: result.error ?? 'Something went wrong. Please try again.',
          });
          return;
        }
        setNotification({
          show: true,
          valid: true,
          title: 'Subscribed',
          message: `You are now subscribed to ${result.data?.subscribedThreadCount ?? 0} thread(s).`,
        });
      } else {
        const result = await bulkUnsubscribeFromServiceRequests({
          serviceRequestIds,
        });
        if (!result.success) {
          setNotification({
            show: true,
            valid: false,
            title: 'Error',
            message: result.error ?? 'Something went wrong. Please try again.',
          });
          return;
        }
        setNotification({
          show: true,
          valid: true,
          title: 'Unsubscribed',
          message: `You are now unsubscribed from ${result.data?.unsubscribedThreadCount ?? 0} thread(s).`,
        });
      }

      onClose();
      await onSuccess?.();
    } finally {
      setPending(null);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isBusy) onClose();
      }}
    >
      <DialogContent hideCloseIcon className="bg-white">
        <header className="flex items-start gap-xl p-3xl">
          <DialogTitle className="min-w-0 flex-1 text-text-primary txt-large-bold">
            Manage subscriptions
          </DialogTitle>
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close"
            className="flex shrink-0 size-[26px] items-center justify-center rounded-sm p-xs text-fg-icon-button hover:bg-bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icons.XClose className="size-5" />
          </button>
        </header>

        <div className="px-3xl">
          <DialogDescription className="txt-small-regular text-text-primary">
            Do you want to subscribe to or unsubscribe from the{' '}
            {serviceRequestIds.length} selected thread(s)?
          </DialogDescription>
        </div>

        <DialogFooter className="border-0 p-3xl">
          <Button
            variant="secondary"
            size="md"
            onClick={() => run('unsubscribe')}
            isLoading={pending === 'unsubscribe'}
            disabled={isBusy}
          >
            <Icons.BellOff01 className="h-4 w-4 mr-2" />
            Unsubscribe
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => run('subscribe')}
            isLoading={pending === 'subscribe'}
            disabled={isBusy}
          >
            <Icons.Bell01 className="h-4 w-4 mr-2" />
            Subscribe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
